import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import ButtonDropdown from "@cloudscape-design/components/button-dropdown"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { ReactNode } from "react"
import { UseFormReturn } from "react-hook-form"
import InlineEditableText from "../../../components/InlineEditableText"
import CategorySection from "./CategorySection"
import { ProtocolFormData } from "./schema"
import { useAssessmentsGrid, VisitField } from "./useAssessmentsGrid"
import VisitMetaCell from "./VisitMetaCell"
import "./AssessmentsGrid.css"

interface AssessmentsGridProps {
  /**
   * React Hook Form instance for the protocol form.
   */
  form: UseFormReturn<ProtocolFormData>

  /**
   * Index of the protocol part being edited.
   */
  partIndex: number

  /**
   * When true, disables all editing interactions.
   */
  readOnly?: boolean

  /**
   * When true, renders without its own Container so it can sit inside an existing card/section.
   */
  embedded?: boolean

  /**
   * Heading for this grid's section. Defaults to "Schedule of Activities"; multi-part protocols
   * pass a per-part label so each part renders under its own heading.
   */
  partLabel?: string
}

/**
 * Interactive grid for managing the Schedule of Activities (SoA).
 *
 * Displays a table with periods/visits as columns and assessments as rows. Supports inline editing
 * of period names, visit metadata, and assessment names. Checkboxes indicate which assessments are
 * scheduled for each visit.
 */
const AssessmentsGrid = ({
  form,
  partIndex,
  readOnly = false,
  embedded = false,
  partLabel = "Schedule of Activities",
}: AssessmentsGridProps) => {
  const grid = useAssessmentsGrid(form, partIndex)

  // Standalone renders as its own Cloudscape Container; embedded drops the card so the grid can
  // sit inside a parent section without nested outlines.
  const section = (content: ReactNode) => {
    const header = <Header variant="h2">{partLabel}</Header>
    return embedded ? (
      <SpaceBetween size="s">
        {header}
        {content}
      </SpaceBetween>
    ) : (
      <Container header={header}>{content}</Container>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Render Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  const isEditingCell = (visitId: string, field: VisitField) =>
    grid.editingCell !== null &&
    grid.editingCell.visitId === visitId &&
    grid.editingCell.field === field

  const renderVisitMetaCell = (visit: { id: string }, field: VisitField, placeholder: string) => {
    const fullVisit = grid.allVisits.find((v) => v.id === visit.id)
    if (!fullVisit) return null
    return (
      <VisitMetaCell
        key={visit.id}
        visit={fullVisit}
        field={field}
        placeholder={placeholder}
        editing={!readOnly && isEditingCell(visit.id, field)}
        draft={grid.cellDraft}
        hovering={!readOnly && grid.hoveringVisit === visit.id}
        onStartEdit={() => !readOnly && grid.startCellEdit(visit.id, field)}
        onDraftChange={grid.setCellDraft}
        onCommit={grid.commitCellEdit}
        onCancel={grid.cancelCellEdit}
        onHover={(id) => !readOnly && grid.setHoveringVisit(id)}
        readOnly={readOnly}
      />
    )
  }

  const renderVisitMetaRow = (
    label: string,
    field: VisitField,
    placeholder: string,
    extraClass?: string,
  ) => (
    <tr className={`meta-row ${extraClass || ""}`}>
      <th className="row-label-col">{label}</th>
      {grid.periods.flatMap((period) =>
        period.visits.map((visit) => renderVisitMetaCell(visit, field, placeholder)),
      )}
    </tr>
  )

  // ─────────────────────────────────────────────────────────────────────────────
  // Empty State
  // ─────────────────────────────────────────────────────────────────────────────

  if (grid.periods.length === 0) {
    if (readOnly) return null

    return section(
      <div className="soa-grid-wrapper">
        <table className="soa-grid">
          <thead>
            <tr className="period-row">
              <th className="row-label-col">Period</th>
            </tr>
            <tr className="visit-row">
              <th className="row-label-col">Visit</th>
            </tr>
            <tr className="meta-row">
              <th className="row-label-col">Study Week</th>
            </tr>
            <tr className="meta-row">
              <th className="row-label-col">Study Day</th>
            </tr>
            <tr className="meta-row window-row">
              <th className="row-label-col">Window (Day)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="empty-body">
                <Box variant="span" padding={{ right: "s" }}>
                  Add a period to get started.
                </Box>
                <Button iconName="add-plus" onClick={() => grid.addPeriod()}>
                  Add Period
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>,
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Main Render
  // ─────────────────────────────────────────────────────────────────────────────

  return section(
    <div className="soa-grid-wrapper">
      <table className="soa-grid">
        <thead ref={grid.theadRef}>
          {/* Period header row */}
          <tr className="period-row">
            <th className="row-label-col">Period</th>
            {grid.periods.map((period, pIdx) => (
              <th key={pIdx} colSpan={period.visits.length} className="period-cell">
                {readOnly ? (
                  <span className="period-name readonly">{period.name}</span>
                ) : (
                  <div className="period-cell-content">
                    <InlineEditableText
                      value={period.name}
                      editing={grid.editingPeriod === pIdx}
                      onEditingChange={(editing) => grid.setEditingPeriod(editing ? pIdx : null)}
                      onSave={(newName) => grid.renamePeriod(pIdx, newName)}
                      onCancel={() => grid.setEditingPeriod(null)}
                      className="period-name"
                    />
                    <ButtonDropdown
                      variant="icon"
                      expandToViewport
                      expandableGroups
                      ariaLabel={`${period.name} period actions`}
                      items={[
                        { id: "add-visit", text: "Add Visit", iconName: "add-plus" },
                        { id: "add-period", text: "Add Period", iconName: "add-plus" },
                        { id: "remove-period", text: "Remove Period", iconName: "remove" },
                        {
                          id: "remove-visit",
                          text: "Remove Visit",
                          disabled: period.visits.length === 1,
                          items: period.visits.map((visit, vIdx) => ({
                            id: `remove-visit-${vIdx}`,
                            text: `Visit ${visit.visit_number || vIdx + 1}`,
                          })),
                        },
                      ]}
                      onItemClick={({ detail }) => {
                        if (detail.id === "add-visit") grid.addVisit(pIdx)
                        else if (detail.id === "add-period") grid.addPeriod(pIdx)
                        else if (detail.id === "remove-period") grid.deletePeriod(pIdx)
                        else if (detail.id.startsWith("remove-visit-")) {
                          grid.deleteVisit(pIdx, Number(detail.id.replace("remove-visit-", "")))
                        }
                      }}
                    />
                  </div>
                )}
              </th>
            ))}
          </tr>

          {/* Visit metadata rows */}
          <tr className="visit-row">
            <th className="row-label-col">Visit</th>
            {grid.periods.flatMap((period) =>
              period.visits.map((visit) => renderVisitMetaCell(visit, "visit_number", "1")),
            )}
          </tr>

          {renderVisitMetaRow("Study Week", "study_week", "0")}
          {renderVisitMetaRow("Study Day", "study_day", "1")}
          {renderVisitMetaRow("Window (Day)", "window_days", "±3", "window-row")}
        </thead>

        <tbody>
          {!readOnly && grid.categories.length === 0 && (
            <tr>
              <td colSpan={grid.allVisits.length + 1} className="empty-body">
                <Box variant="span" padding={{ right: "s" }}>
                  Add a category to define assessments.
                </Box>
                <Button iconName="add-plus" onClick={() => grid.addCategory()}>
                  Add Category
                </Button>
              </td>
            </tr>
          )}

          {grid.categories.map((category) => {
            const categoryAssessments = grid.assessments
              .map((a, i) => ({ ...a, idx: i }))
              .filter((a) => (a.category || "Uncategorized") === category)

            return (
              <CategorySection
                key={category}
                category={category}
                categoryAssessments={categoryAssessments}
                allVisits={grid.allVisits}
                theadHeight={grid.theadHeight}
                editingCategory={grid.editingCategory === category}
                isNewCategory={grid.newCategoryName === category}
                addingAssessment={grid.addingAssessment}
                newAssessmentName={grid.newAssessmentName}
                hoveringVisit={grid.hoveringVisit}
                readOnly={readOnly}
                onEditCategory={(editing) => {
                  if (editing) {
                    grid.setEditingCategory(category)
                  } else {
                    grid.setEditingCategory(null)
                    grid.setNewCategoryName(null)
                  }
                }}
                onSaveCategory={(newName) => {
                  if (grid.newCategoryName === category) {
                    grid.renameNewCategory(newName)
                  } else {
                    grid.renameCategory(category, newName)
                  }
                }}
                onCancelEditCategory={() => {
                  grid.setEditingCategory(null)
                  grid.setNewCategoryName(null)
                }}
                onDeleteCategory={() => grid.deleteCategory(category)}
                onStartAddingAssessment={() => grid.setAddingAssessment(category)}
                onNewAssessmentNameChange={grid.setNewAssessmentName}
                onAddAssessment={() => grid.addAssessmentToCategory(category)}
                onCancelAddAssessment={() => grid.setAddingAssessment(null)}
                onUpdateAssessment={grid.updateAssessment}
                onDeleteAssessment={(idx) => grid.deleteAssessment(idx)}
                onToggleVisit={grid.toggleVisit}
                onHoverVisit={grid.setHoveringVisit}
              />
            )
          })}
        </tbody>
      </table>

      {/* Prompted when clicking away from a cell with uncommitted changes */}
      {grid.showSavePrompt && (
        <Modal
          visible
          onDismiss={grid.keepEditingCell}
          header="Save changes?"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" formAction="none" onClick={grid.cancelCellEdit}>
                  Discard changes
                </Button>
                <Button variant="primary" formAction="none" onClick={grid.commitCellEdit}>
                  Save changes
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          You have unsaved changes in this cell. Save them, or cancel to discard.
        </Modal>
      )}
    </div>,
  )
}

export default AssessmentsGrid
